/*!

=========================================================
* Vision UI Free React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-free-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)
* Licensed under MIT (https://github.com/creativetimofficial/vision-ui-free-react/blob/master LICENSE.md)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

import { useState, useEffect, useCallback } from "react";

// react-router components
import { useLocation, Link, useHistory } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @material-ui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Icon from "@mui/material/Icon";
import Badge from "@mui/material/Badge";
import ClickAwayListener from "@mui/material/ClickAwayListener";

// Vision UI Dashboard React components
import VuiBox from "components/VuiBox";
import VuiBadge from "components/VuiBadge";
import VuiTypography from "components/VuiTypography";
import VuiInput from "components/VuiInput";

// Vision UI Dashboard React example components
import NotificationItem from "examples/Items/NotificationItem";

// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";

// Vision UI Dashboard React context
import {
  useVisionUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";

import Breadcrumbs from "examples/Breadcrumbs";

// Images
import { useCompany } from "../../../context/CompanyContext";
import { signOut as firebaseSignOut } from "firebase/auth";
import { auth } from "../../../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { useAuthz } from "context/AuthzContext";
import { apiRequest, listClientProfiles } from "services/tenantUserAdminService";
import { TC } from "../../../tokens/text";

// ── Notification → route mapping ──────────────────────────────────────────────

function getNotifRoute(n) {
  const clientId  = n.clientId  || n.client_id  || null;
  const type      = n.type      || "";
  const change    = n.changeType || n.change_type || "";

  // Inbound email → client profile Communications tab
  if (type === "inbound_email") {
    return clientId
      ? { pathname: `/client-profiles/${encodeURIComponent(clientId)}`, state: { tab: 4 } }
      : "/client-profiles";
  }

  // Director changes → Directors page pre-filtered to that client
  if (
    type === "director_change" ||
    change === "director_appointed" ||
    change === "director_resigned" ||
    change === "director_changed"
  ) {
    return clientId ? `/directors?clientId=${encodeURIComponent(clientId)}` : "/directors";
  }

  // PSC changes → Client profile (PSC is displayed there)
  if (
    change === "psc_appointed" ||
    change === "psc_ceased"    ||
    change === "psc_changed"
  ) {
    return clientId ? `/client-profiles/${encodeURIComponent(clientId)}` : "/client-profiles";
  }

  // Company-level structural changes → Client profiles page
  if (
    change === "company_name_changed"           ||
    change === "company_status_changed"         ||
    change === "address_changed"                ||
    change === "sic_codes_changed"              ||
    change === "accounts_dates_changed"         ||
    change === "confirmation_statement_changed"
  ) {
    return clientId ? `/client-profiles/${encodeURIComponent(clientId)}` : "/client-profiles";
  }

  // Bank / consent notifications → Bank Feeds page
  if (
    type === "consent_expiring"  ||
    type === "consent_expired"   ||
    type === "bank_feed"         ||
    type === "consent"
  ) {
    return "/bank-feeds";
  }

  // Exception / transaction notifications → Exceptions page
  if (type === "exception" || type === "transaction") {
    return "/exceptions";
  }

  // VAT-related
  if (type === "vat") return "/vat-return";

  // Period close
  if (type === "period_close") return "/period-close";

  // Generic CH change fallback
  if (type === "ch_change") return clientId ? `/client-profiles/${encodeURIComponent(clientId)}` : "/client-profiles";

  // Fallback: dashboard
  return "/dashboard";
}

const CHANGE_TYPE_LABELS = {
  director_appointed:             "Director Appointed",
  director_resigned:              "Director Resigned",
  director_changed:               "Director Changed",
  psc_appointed:                  "PSC Appointed",
  psc_ceased:                     "PSC Ceased",
  psc_changed:                    "PSC Changed",
  company_name_changed:           "Company Name Changed",
  company_status_changed:         "Company Status Changed",
  address_changed:                "Address Changed",
  sic_codes_changed:              "SIC Codes Changed",
  accounts_dates_changed:         "Accounts Dates Changed",
  confirmation_statement_changed: "Confirmation Statement Changed",
};

function notifIcon(type, severity) {
  const iconSx = { color: TC.primary, fontSize: "1rem !important", lineHeight: 1 };
  if (type === "inbound_email")  return <Icon sx={{ ...iconSx, color: "#a78bfa" }}>mail</Icon>;
  if (type === "ch_change") {
    if (severity === "high")   return <Icon sx={iconSx}>warning</Icon>;
    if (severity === "medium") return <Icon sx={iconSx}>notifications_active</Icon>;
    return <Icon sx={iconSx}>info</Icon>;
  }
  if (type === "director_change") return <Icon sx={iconSx}>person</Icon>;
  return <Icon sx={iconSx}>notifications</Icon>;
}

function notifColor(severity) {
  if (severity === "high")   return "error";
  if (severity === "medium") return "warning";
  return "info";
}

function notifTitle(n) {
  if (n.type === "inbound_email") {
    return [n.clientName || n.fromEmail || "Client", n.subject || "New email received"];
  }
  if (n.changeType || n.change_type) {
    const label = CHANGE_TYPE_LABELS[n.changeType || n.change_type] || (n.changeType || n.change_type);
    const client = n.clientName || n.companyNumber || "";
    return [client, label];
  }
  if (n.type === "director_change") {
    const action = n.changeType === "resigned" ? "resigned" : "appointed";
    return [n.directorName || "Director", action];
  }
  return ["Notification", n.message || ""];
}

function timeAgo(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1)  return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function DashboardNavbar({ absolute = false, light = false, isMini = false, customTitle = null, customRoutes = null }) {
  return null;
}

DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
  customTitle: PropTypes.string,
  customRoutes: PropTypes.array,
};

export default DashboardNavbar;

